import React, { useEffect, useRef, useState } from 'react';
// import { Html5Qrcode } from 'html5-qrcode'; // Removed to use CDN version
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";

const BarcodeScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scannerRef = useRef(null);
  const scannerId = "reader";

  useEffect(() => {
    let isMounted = true;

    if (isOpen) {
      // Initialize scanner
      const initializeScanner = async () => {
        try {
          // If a scanner instance already exists, stop it first (cleanup from previous run)
          if (scannerRef.current) {
            await scannerRef.current.stop().catch(err => console.warn("Stop failed", err));
            await scannerRef.current.clear().catch(err => console.warn("Clear failed", err));
            scannerRef.current = null;
          }

          // Ensure the library is loaded
          if (!window.Html5Qrcode) {
             setIsLoading(true);
             // Try to load script dynamically if not present
             await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = "https://unpkg.com/html5-qrcode";
                script.type = "text/javascript";
                script.onload = resolve;
                script.onerror = () => reject(new Error("Failed to load scanner script"));
                document.head.appendChild(script);
             });
             setIsLoading(false);
          }

          if (!window.Html5Qrcode) {
              setError("Scanner library failed to load. Please check your connection.");
              return;
          }

          const html5QrCode = new window.Html5Qrcode(scannerId);
          scannerRef.current = html5QrCode;

          await html5QrCode.start(
            { facingMode: "environment" },
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
            },
            (decodedText) => {
              if (isMounted) {
                onScanSuccess(decodedText);
                onClose(); // Close modal on success
              }
            },
            (errorMessage) => {
              // parse error, ignore it.
            }
          );
        } catch (err) {
          if (isMounted) {
            console.error("Error starting scanner", err);
            if (err.message === "Failed to load scanner script") {
                setError("Failed to load scanner library. Please check your internet connection.");
            } else {
                setError("Could not start camera. Please ensure you have given permission.");
            }
            setIsLoading(false);
          }
        }
      };

      // Small delay to ensure the DOM element exists
      setTimeout(initializeScanner, 100);
    } else {
      // Cleanup if modal is closed
      const cleanup = async () => {
        if (scannerRef.current) {
          try {
            const scanner = scannerRef.current;
            scannerRef.current = null; // Prevent double cleanup
            await scanner.stop();
            await scanner.clear();
          } catch (err) {
            console.warn("Failed to stop scanner", err);
          }
        }
      };
      cleanup();
    }

    return () => {
      isMounted = false;
      // Cleanup on unmount
      if (scannerRef.current) {
         const scanner = scannerRef.current;
         scannerRef.current = null; // Prevent double cleanup
         scanner.stop()
            .then(() => scanner.clear())
            .catch(err => console.warn("Cleanup failed", err));
      }
    };
  }, [isOpen, onClose, onScanSuccess]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Scan Barcode</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center space-y-4">
            <div id={scannerId} className="w-full h-80 bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center">
                {isLoading && <p className="text-gray-500">Loading scanner...</p>}
            </div>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScannerModal;
